import axios from 'axios';

const API_BASE = `${import.meta.env.VITE_API_URL || ''}/api/v1/inventory/serials`;

function getAuthHeaders() {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId') || '';
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'x-company-id': companyId,
  };
}

export interface SerialMovement {
  id: string;
  serial_id: string;
  movement_type: 'IN' | 'OUT' | 'TRANSFER' | 'RETURN' | 'STATUS_CHANGE' | 'ADJUSTMENT';
  reference_type: string;
  reference_id?: string;
  reference_no?: string;
  from_warehouse_id?: string;
  from_warehouse_name?: string;
  to_warehouse_id?: string;
  to_warehouse_name?: string;
  from_status?: string;
  to_status?: string;
  party_id?: string;
  party_name?: string;
  done_by?: string;
  done_by_name?: string;
  notes?: string;
  created_at: string;
}

export interface SerialItem {
  id: string;
  company_id: string;
  item_id: string;
  item_name?: string;
  item_code?: string;
  serial_number: string;
  vendor_serial_number?: string;
  imei?: string;
  batch_id?: string;
  batch_number?: string;
  grn_id?: string;
  grn_number?: string;
  grn_date?: string;
  purchase_rate?: number;
  warehouse_id: string;
  warehouse_name?: string;
  shelf_location?: string;
  status: 'IN_STOCK' | 'RESERVED' | 'SOLD' | 'RETURNED' | 'DEFECTIVE' | 'ON_HOLD' | 'IN_REPAIR' | 'SCRAPPED' | 'DEMO';
  condition: 'GOOD' | 'DAMAGED_BOX' | 'DEFECTIVE' | 'REFURBISHED';
  warranty_expiry_date?: string;
  customer_id?: string;
  customer_name?: string;
  supplier_name?: string;
  sales_invoice_id?: string;
  invoice_number?: string;
  invoice_date?: string;
  sale_rate?: number;
  po_number?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  days_left?: number;
}

export interface SerialListParams {
  itemId?: string;
  warehouseId?: string;
  status?: string;
  condition?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface SerialListResponse {
  success: boolean;
  data: SerialItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface SerialDetailResponse {
  success: boolean;
  serial: SerialItem;
  movements: SerialMovement[];
}

export const serialApi = {
  list: async (params?: SerialListParams): Promise<SerialListResponse> => {
    const res = await axios.get(API_BASE, {
      params,
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  getById: async (id: string): Promise<SerialDetailResponse> => {
    const res = await axios.get(`${API_BASE}/${id}`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  trace: async (query: string): Promise<{ success: boolean; data: SerialDetailResponse }> => {
    const res = await axios.get(`${API_BASE}/trace/${encodeURIComponent(query)}`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  getAvailable: async (itemId: string, warehouseId?: string): Promise<{ success: boolean; data: SerialItem[] }> => {
    const res = await axios.get(`${API_BASE}/available`, {
      params: { itemId, warehouseId },
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  getWarrantyExpiring: async (days = 90): Promise<{ success: boolean; data: SerialItem[] }> => {
    const res = await axios.get(`${API_BASE}/warranty/expiring`, {
      params: { days },
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  generateSerialNumber: async (itemCode?: string, prefix?: string): Promise<{ success: boolean; serialNumber: string }> => {
    const res = await axios.post(
      `${API_BASE}/generate`,
      { itemCode, prefix },
      { headers: getAuthHeaders() }
    );
    return res.data;
  },

  updateStatus: async (
    id: string,
    payload: { status?: string; condition?: string; notes?: string }
  ): Promise<{ success: boolean; data: SerialItem }> => {
    const res = await axios.patch(`${API_BASE}/${id}/status`, payload, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  transfer: async (
    id: string,
    payload: { toWarehouseId: string; notes?: string }
  ): Promise<{ success: boolean; data: SerialItem }> => {
    const res = await axios.patch(`${API_BASE}/${id}/transfer`, payload, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },
};
